import { FC, useMemo } from 'react';

import { ITradeAsset, ITradeDetails } from 'models/trade/i-trade-details.interface';

import { ROUTES } from 'constants/routes.constant';

import { useNftCardBackData } from 'components/nftCard/_new/hooks';
import NftCard from 'components/nftCard/_new/nftCard/NftCard';
import NftCardLabelPanel from 'components/nftCard/_new/nftCardLabelPanel/NftCardLabelPanel';
import NftCardValue from 'components/nftCard/_new/nftCardValue/NftCardValue';
import { INftCardAsset } from 'components/nftCard/_new/types';

import { useMappedIncome } from 'hooks/useMappedIncome';
import { useMappedPrice } from 'hooks/useMappedPrice';
import { useTradePrices } from 'hooks/useTradePrices';
import { useTraits } from 'hooks/useTraits';

const MarketNftCard: FC<{ trade: ITradeDetails }> = ({ trade }) => {
  const { income: lastWeekIncome, currency: lastWeekIncomeCurrency } = useMappedIncome(trade!.asset.last_week_income);
  const { currencyAmount: lastSaleCurrencyAmount, currency: lastSaleCurrency } = useMappedPrice(
    trade!.asset.last_sale_price
  );
  const { price, currency } = useTradePrices(trade);
  const { currencyAmount: lowestPrice, currency: lowestPriceCurrency } = useMappedPrice(
    trade.asset.lowest_possible_price
  );
  const { id, category } = useTraits(trade.asset.traits);
  const { data, loading, onCallback } = useNftCardBackData(trade.asset.token_id);

  const currentPrice = useMemo(() => {
    return {
      currency: lowestPriceCurrency || currency,
      value: lowestPrice || price,
    };
  }, [lowestPrice, lowestPriceCurrency, currency, price]);

  const lastSalePerShare = {
    currency: lastSaleCurrency,
    value: lastSaleCurrencyAmount,
  };

  const adaptAsset = (asset: ITradeAsset): INftCardAsset => ({
    id,
    category,
    animationUrl: asset.animation_url,
    imageUrl: asset.image_original_url,
    markerUrl: asset.marker_url,
    shares: asset.shares_currently_on_sale,
    name: asset.name,
    address: asset.address,
    mintedShares: asset.total_shares,
    ...data,
  });

  return (
    <div className="[perspective:800px]">
      <NftCard
        size="small"
        asset={adaptAsset(trade.asset)}
        asLinkTo={`${ROUTES.TRADES}/preview/${trade.asset.token_id}`}
        onCallback={onCallback}
        loading={loading}
        className="tw-p-3"
        frontBackgroundClassName="tw-bg-backgroundColorMedium"
        backBackgroundClassName="tw-bg-backgroundColorMedium"
      >
        <div className="tw-mt-3 tw-grid tw-gap-1">
          <NftCardLabelPanel label="Royalties Last 7d">
            <NftCardValue currency={lastWeekIncomeCurrency} value={lastWeekIncome} />
          </NftCardLabelPanel>
          <NftCardLabelPanel label="Current Price">
            <NftCardValue {...currentPrice} />
          </NftCardLabelPanel>
          <NftCardLabelPanel label="Last Sale (per share)">
            <NftCardValue {...lastSalePerShare} />
          </NftCardLabelPanel>
        </div>
      </NftCard>
    </div>
  );
};

export default MarketNftCard;
